import { useLogto } from '@logto/react';
import { useEffect, useMemo, useState } from 'react';
import { createApiClient, type MonthReportResponse, type ReportRow } from '../api/client';
import { addMonths, change, currentMonthInCaboVerde, formatPct, type Change, type ChangeDirection } from '../reportMonth';
import { ErrorBanner } from '../ui/ErrorBanner';
import { channelLabel, formatEur, formatMonthLabel, formatMonthName } from '../ui/format';

const perSeat = (operatorNet: number, pax: number) => (pax > 0 ? operatorNet / pax : 0);

// A CONTIGUOUS month window (newest first) from 24 months back to 2 months ahead of the current Cabo Verde
// month, extended to cover the selected month when prev/next has stepped past that range — so the dropdown
// never has a gap and the selection is always a real option. A select works in every browser, unlike input
// type=month (unsupported in desktop Firefox and Safari, which Tiago uses).
function monthOptions(selected: string): string[] {
  const current = currentMonthInCaboVerde();
  const anchors = [addMonths(current, -24), addMonths(current, 2), selected].sort(); // YYYY-MM sorts chronologically
  const lo = anchors[0];
  const hi = anchors[anchors.length - 1];
  const out: string[] = [];
  for (let m = lo; m <= hi; m = addMonths(m, 1)) {
    out.push(m);
  }
  return out.reverse();
}

export function Dashboard() {
  const { getAccessToken } = useLogto();
  const api = useMemo(() => createApiClient(getAccessToken), [getAccessToken]);
  const [month, setMonth] = useState<string | null>(() => currentMonthInCaboVerde());
  const [allTime, setAllTime] = useState<ReportRow[]>([]);
  const [report, setReport] = useState<MonthReportResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Stale-response guard: switching month must not let an earlier, slower response overwrite a later one.
  useEffect(() => {
    let active = true;
    const fail = (err: unknown) => {
      if (active) setError((err as Error).message);
    };
    if (month === null) {
      api.getReport()
        .then((rows) => {
          if (!active) return;
          setAllTime(rows);
          setReport(null);
          setError(null);
        })
        .catch(fail);
    } else {
      api.getMonthReport(month)
        .then((rep) => {
          if (!active) return;
          setReport(rep);
          setError(null);
        })
        .catch(fail);
    }
    return () => {
      active = false;
    };
  }, [api, month]);

  return (
    <section className="page">
      <header className="page-head">
        <h1>Receita por canal</h1>
        <p className="subtle">Em qualquer canal online, o operador fica com 25 € por lugar.</p>
      </header>

      <div className="month-toolbar">
        <button className="btn" onClick={() => setMonth((m) => addMonths(m ?? currentMonthInCaboVerde(), -1))} disabled={month === null}>← Mês anterior</button>
        <select
          value={month ?? ''}
          onChange={(e) => setMonth(e.target.value || currentMonthInCaboVerde())}
          aria-label="Mês"
          disabled={month === null}
        >
          {month === null && <option value="">Desde sempre</option>}
          {monthOptions(month ?? currentMonthInCaboVerde()).map((m) => (
            <option key={m} value={m}>{formatMonthLabel(m)}</option>
          ))}
        </select>
        <button className="btn" onClick={() => setMonth((m) => addMonths(m ?? currentMonthInCaboVerde(), 1))} disabled={month === null}>Mês seguinte →</button>
        <button className={month === null ? 'btn btn-primary' : 'btn'} onClick={() => setMonth(null)}>Desde sempre</button>
        {month === null ? (
          <button className="btn btn-ghost" onClick={() => setMonth(currentMonthInCaboVerde())}>Mês atual</button>
        ) : null}
      </div>

      <ErrorBanner message={error} />

      {month === null ? (
        <AllTimeView rows={allTime} />
      ) : report ? (
        <MonthView report={report} />
      ) : (
        <p className="subtle">A carregar…</p>
      )}
    </section>
  );
}

function Delta({ c, prevMonth, format, compact = false, decimals = 0 }: { c: Change; prevMonth: string; format: (n: number) => string; compact?: boolean; decimals?: number }) {
  const suffix = compact ? '' : ` vs ${formatMonthName(prevMonth)}`;
  if (c.pct === null) {
    return <span className="delta delta-flat">—{suffix}</span>;
  }
  // Base the label, sign, arrow and colour on the value as it is DISPLAYED (rounded to the metric's precision —
  // cents for money), so a change that rounds to zero reads "→ 0,00 € (0%)", never a green "+0,00 €" or a
  // "-0,00 €". `|| 0` collapses a rounded -0 so the formatted label has no spurious minus sign.
  const factor = 10 ** decimals;
  const shownAbs = (Math.round(c.abs * factor) || 0) / factor;
  const direction: ChangeDirection = shownAbs > 0 ? 'up' : shownAbs < 0 ? 'down' : 'flat';
  const arrow = direction === 'up' ? '↑' : direction === 'down' ? '↓' : '→';
  const sign = shownAbs > 0 ? '+' : '';
  return (
    <span className={`delta delta-${direction}`}>
      {arrow} {sign}{format(shownAbs)} ({formatPct(c.pct)}){suffix}
    </span>
  );
}

function AllTimeView({ rows }: { rows: ReportRow[] }) {
  const totalOperator = rows.reduce((n, r) => n + r.operatorNet, 0);
  const totalPlatform = rows.reduce((n, r) => n + r.platformNet, 0);
  return (
    <>
      <div className="stat-grid">
        {rows.map((r) => (
          <div key={r.channel} className={r.channel === 'WalkIn' ? 'card stat-card' : 'card stat-card accent'}>
            <div className="stat-label">{channelLabel(r.channel)}</div>
            <div className="stat money">{formatEur(perSeat(r.operatorNet, r.pax))}</div>
            <div className="stat-sub">por lugar · {r.pax} pax</div>
          </div>
        ))}
        {rows.length === 0 ? <div className="card subtle">Sem reservas ainda.</div> : null}
      </div>

      <section className="card">
        <h2>Detalhe</h2>
        <div className="table-scroll">
          <table className="data">
            <thead>
              <tr>
                <th>Canal</th>
                <th className="num">Reservas</th>
                <th className="num">Pax</th>
                <th className="num">Cliente</th>
                <th className="num">Operador</th>
                <th className="num">Plataforma</th>
                <th className="num">Operador/lugar</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.channel}>
                  <td>{channelLabel(r.channel)}</td>
                  <td className="num">{r.bookings}</td>
                  <td className="num">{r.pax}</td>
                  <td className="num money">{formatEur(r.customerPaid)}</td>
                  <td className="num money">{formatEur(r.operatorNet)}</td>
                  <td className="num money">{formatEur(r.platformNet)}</td>
                  <td className="num money">{formatEur(perSeat(r.operatorNet, r.pax))}</td>
                </tr>
              ))}
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="subtle">Sem reservas ainda.</td>
                </tr>
              ) : null}
            </tbody>
            <tfoot>
              <tr>
                <td>Total</td>
                <td className="num"></td>
                <td className="num"></td>
                <td className="num"></td>
                <td className="num money">{formatEur(totalOperator)}</td>
                <td className="num money">{formatEur(totalPlatform)}</td>
                <td className="num"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </section>
    </>
  );
}

function MonthView({ report }: { report: MonthReportResponse }) {
  const { rows, totals, previous } = report;
  return (
    <>
      <p className="subtle month-label">{formatMonthLabel(report.month)}</p>

      <div className="stat-grid">
        <div className="card stat-card accent">
          <div className="stat-label">Operador recebeu</div>
          <div className="stat money">{formatEur(totals.operatorNet)}</div>
          <div className="stat-sub">
            <Delta c={change(totals.operatorNet, previous.totals.operatorNet)} prevMonth={previous.month} format={formatEur} decimals={2} />
          </div>
        </div>
        <div className="card stat-card">
          <div className="stat-label">Reservas</div>
          <div className="stat">{totals.bookings}</div>
          <div className="stat-sub">
            <Delta c={change(totals.bookings, previous.totals.bookings)} prevMonth={previous.month} format={(n) => String(n)} />
          </div>
        </div>
        <div className="card stat-card">
          <div className="stat-label">Clientes</div>
          <div className="stat">{totals.distinctCustomers}</div>
          <div className="stat-sub">
            <Delta c={change(totals.distinctCustomers, previous.totals.distinctCustomers)} prevMonth={previous.month} format={(n) => String(n)} />
          </div>
        </div>
      </div>

      <section className="card">
        <h2>Detalhe</h2>
        <div className="table-scroll">
          <table className="data">
            <thead>
              <tr>
                <th>Canal</th>
                <th className="num">Reservas</th>
                <th className="num">Pax</th>
                <th className="num">Clientes</th>
                <th className="num">Cliente pagou</th>
                <th className="num">Operador</th>
                <th className="num">Plataforma</th>
                <th className="num">Operador/lugar</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.channel}>
                  <td>{channelLabel(r.channel)}</td>
                  <td className="num">{r.bookings}</td>
                  <td className="num">{r.pax}</td>
                  <td className="num">{r.distinctCustomers}</td>
                  <td className="num money">{formatEur(r.customerPaid)}</td>
                  <td className="num money">{formatEur(r.operatorNet)}</td>
                  <td className="num money">{formatEur(r.platformNet)}</td>
                  <td className="num money">{formatEur(perSeat(r.operatorNet, r.pax))}</td>
                </tr>
              ))}
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="subtle">Sem reservas neste mês.</td>
                </tr>
              ) : null}
            </tbody>
            <tfoot>
              <tr>
                <td>Total</td>
                <td className="num">{totals.bookings}</td>
                <td className="num">{totals.pax}</td>
                <td className="num">{totals.distinctCustomers}</td>
                <td className="num money">{formatEur(totals.customerPaid)}</td>
                <td className="num money">{formatEur(totals.operatorNet)}</td>
                <td className="num money">{formatEur(totals.platformNet)}</td>
                <td className="num money">{totals.pax > 0 ? formatEur(perSeat(totals.operatorNet, totals.pax)) : '—'}</td>
              </tr>
              <tr className="delta-row">
                <td className="subtle">vs {formatMonthName(previous.month)}</td>
                <td className="num"><Delta c={change(totals.bookings, previous.totals.bookings)} prevMonth={previous.month} format={(n) => String(n)} compact /></td>
                <td className="num"><Delta c={change(totals.pax, previous.totals.pax)} prevMonth={previous.month} format={(n) => String(n)} compact /></td>
                <td className="num"><Delta c={change(totals.distinctCustomers, previous.totals.distinctCustomers)} prevMonth={previous.month} format={(n) => String(n)} compact /></td>
                <td className="num"><Delta c={change(totals.customerPaid, previous.totals.customerPaid)} prevMonth={previous.month} format={formatEur} decimals={2} compact /></td>
                <td className="num"><Delta c={change(totals.operatorNet, previous.totals.operatorNet)} prevMonth={previous.month} format={formatEur} decimals={2} compact /></td>
                <td className="num"><Delta c={change(totals.platformNet, previous.totals.platformNet)} prevMonth={previous.month} format={formatEur} decimals={2} compact /></td>
                <td className="num">{totals.pax > 0
                  ? <Delta c={change(perSeat(totals.operatorNet, totals.pax), perSeat(previous.totals.operatorNet, previous.totals.pax))} prevMonth={previous.month} format={formatEur} decimals={2} compact />
                  : '—'}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </section>
    </>
  );
}
